import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Car } from "lucide-react";

const POLL_MS = 20000;

const timeAgo = (iso) => {
  if (!iso) return "sin señal";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 30) return "ahora";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function OnlineDriversBadge() {
  const { user } = useAuth();
  const [drivers, setDrivers] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users/drivers");
      setDrivers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("OnlineDriversBadge.load failed:", e);
    }
  }, []);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "dispatcher")) return;
    load();
    const pl = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(pl);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, load]);

  if (!user || (user.role !== "admin" && user.role !== "dispatcher")) return null;

  const free = drivers.filter((d) => d.driver_status === "free").length;
  const busy = drivers.filter((d) => d.driver_status === "busy").length;
  const online = free + busy;
  const total = drivers.length;

  // sort: free first, busy next, offline last
  const order = { free: 0, busy: 1, offline: 2 };
  const sorted = [...drivers].sort(
    (a, b) => (order[a.driver_status] ?? 3) - (order[b.driver_status] ?? 3) ||
              (a.name || "").localeCompare(b.name || "")
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid="online-drivers-badge"
            className="flex items-center gap-1.5 h-7 px-2 rounded-full bg-secondary/60 border border-border cursor-default select-none hover:bg-secondary transition-colors"
          >
            <Car className="w-3.5 h-3.5 text-primary" />
            <span className="font-mono text-xs font-bold text-white tabular-nums">
              {online}
              <span className="text-muted-foreground font-normal">/{total}</span>
            </span>
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                online > 0 ? "bg-emerald-400 animate-pulse" : "bg-muted-foreground"
              }`}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="p-0 max-w-xs">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-xs font-semibold">Choferes conectados</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
              <span className="text-emerald-400 font-bold">{free}</span> libres ·{" "}
              <span className="text-amber-400 font-bold">{busy}</span> ocupados ·{" "}
              <span className="text-muted-foreground font-bold">{total - online}</span> off
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {sorted.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-3 px-3">Sin choferes</div>
            )}
            {sorted.map((d) => {
              const st = d.driver_status || "offline";
              const dotColor =
                st === "free" ? "bg-emerald-400" :
                st === "busy" ? "bg-amber-400" : "bg-muted-foreground";
              return (
                <div
                  key={d.id}
                  className="flex items-center gap-2 px-3 py-1 text-xs"
                  data-testid={`online-drivers-row-${d.id}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                  <span className="truncate flex-1 text-white">{d.name}</span>
                  <span className="text-[10px] font-mono text-muted-foreground uppercase">
                    {st === "offline" ? "off" : timeAgo(d.last_seen)}
                  </span>
                </div>
              );
            })}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
